/* GLOP v2 chrome effects (loaded by glop2-header and glop2-footer; runs once).
   - Header wordmark: melts on hover / focus, firms back up on leave.
   - Footer lava wordmark: letters drift (CSS) while a goo filter melts them; stirring adds melt.
   - Glop mode: greyscale + pink multiply over the whole page, remembered per visitor.
   Ported from the designer's artifact (logo-warp, lava, glop-mode). */
(() => {
  if (window.__glop2Chrome) return;
  window.__glop2Chrome = true;

  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;

  /* ---------- Glop mode ---------- */
  function initGlopMode() {
    const buttons = [...document.querySelectorAll('[data-glop-mode]')];
    if (!buttons.length) {
      root.classList.remove('glop-on');
      return;
    }
    if (!document.querySelector('.glop2-glop-gray')) {
      // Straight onto <body>: inside the header's stacking context the multiply
      // would only blend with the header.
      root.classList.add('glop-instant');
      for (const cls of ['glop2-glop-gray', 'glop2-glop-tint']) {
        const layer = document.createElement('div');
        layer.className = cls;
        layer.setAttribute('aria-hidden', 'true');
        document.body.appendChild(layer);
      }
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('glop-instant')));
    }
    const sync = () => {
      const on = root.classList.contains('glop-on');
      buttons.forEach((b) => b.setAttribute('aria-pressed', on ? 'true' : 'false'));
    };
    const set = (on) => {
      root.classList.toggle('glop-on', on);
      sync();
      try {
        localStorage.setItem('glop-mode', on ? '1' : '0');
      } catch (e) {}
    };
    buttons.forEach((b) => b.addEventListener('click', () => set(!root.classList.contains('glop-on'))));
    sync();
  }

  /* ---------- Header wordmark melt ---------- */
  function initLogoWarp() {
    const wrap = document.querySelector('.glop2-nav__logo--live');
    const svg = document.getElementById('glop2-logo-warp');
    if (!wrap || !svg || still) return;
    const [turb, , disp, blur, goo] = svg.children;
    const parts = [...wrap.querySelectorAll('.glop2-wm__letter')];
    // Letter boxes in the drawing's user units (G, L, O, P)
    const B = [[0, 54, 386, 238], [321, 0, 468, 96], [461, 8, 770, 200], [762, 2, 912, 274]];
    const mx = 456, my = 137;
    const bodies = parts.map(() => ({ x: 0, y: 0, vx: 0, vy: 0 }));
    let energy = 0, melt = 0, last = null, running = false, hover = false;

    const on = () => { hover = true; go(); };
    const off = () => { hover = false; last = null; };
    wrap.addEventListener('pointerenter', on);
    wrap.addEventListener('pointerleave', off);
    wrap.addEventListener('focus', on);
    wrap.addEventListener('blur', off);
    wrap.addEventListener('pointermove', (e) => {
      const now = performance.now();
      if (last) energy = Math.min(1, energy + (Math.hypot(e.clientX - last.x, e.clientY - last.y) / Math.max(8, now - last.t)) * 0.14);
      last = { x: e.clientX, y: e.clientY, t: now };
      go();
    });

    function go() {
      if (running) return;
      running = true;
      wrap.classList.add('is-warping');
      requestAnimationFrame(tick);
    }

    function tick(t) {
      energy *= 0.96;
      melt += ((hover ? 0.45 : 0) + energy * 0.55 - melt) * 0.08;
      // Everything is in the SVG's user units (the drawing is ~935 units wide, like the artifact's logo)
      const w = 935;
      const upp = w / (wrap.getBoundingClientRect().width || 256); // user units per screen px
      const sigma = w * (0.003 + melt * 0.011), slope = Math.max(24, sigma * 9);
      blur.setAttribute('stdDeviation', sigma.toFixed(2));
      goo.setAttribute('values', `1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${slope.toFixed(1)} ${(-slope * (0.45 - melt * 0.24)).toFixed(1)}`);
      disp.setAttribute('scale', (w * melt * 0.06).toFixed(2));
      const f = (2.2 / w) * (1 + Math.sin(t / 1100) * 0.15);
      turb.setAttribute('baseFrequency', `${f.toFixed(5)} ${(f * 1.6).toFixed(5)}`);
      parts.forEach((el, i) => {
        const b = bodies[i], [x0, y0, x1, y1] = B[i], cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
        b.vx += ((cx - mx) / 935) * energy * 1.4 + Math.cos(t / (700 + i * 150) + i) * melt * 0.18 - b.x * 0.03;
        b.vy += ((cy - my) / 290) * energy * 1.4 + Math.sin(t / (600 + i * 130) + i * 2) * melt * 0.22 - b.y * 0.03;
        b.vx *= 0.9; b.vy *= 0.9; b.x += b.vx; b.y += b.vy;
        el.style.translate = `${(b.x * upp).toFixed(2)}px ${(b.y * upp).toFixed(2)}px`;
      });
      const moving = bodies.some((b) => Math.abs(b.x) + Math.abs(b.y) > 0.1);
      if (hover || melt > 0.01 || moving) {
        requestAnimationFrame(tick);
      } else {
        running = false;
        wrap.classList.remove('is-warping');
        parts.forEach((el) => (el.style.translate = ''));
        bodies.forEach((b) => Object.assign(b, { x: 0, y: 0, vx: 0, vy: 0 }));
        melt = 0;
      }
    }
  }

  /* ---------- Footer lava wordmark ---------- */
  function initLava() {
    const mark = document.querySelector('.glop2-footer__lava');
    const filter = document.getElementById('glop2-lava');
    if (!mark || !filter || still) return;
    const turb = filter.querySelector('feTurbulence');
    const disp = filter.querySelector('feDisplacementMap');
    const blur = filter.querySelector('feGaussianBlur');
    const goo = filter.querySelector('feColorMatrix');
    const host = mark.closest('.glop2-footer__mark') || mark;
    const W = 911.5; // user units
    const baseScale = W * 0.018;
    let energy = 0, last = null, visible = false, running = false;

    mark.classList.add('is-lava');

    host.addEventListener('pointermove', (e) => {
      const now = performance.now();
      if (last) energy = Math.min(1, energy + (Math.hypot(e.clientX - last.x, e.clientY - last.y) / Math.max(8, now - last.t)) * 0.16);
      last = { x: e.clientX, y: e.clientY, t: now };
    });
    host.addEventListener('pointerleave', () => (last = null));

    new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      mark.classList.toggle('is-paused', !visible); // the CSS letter drift stops offscreen too
      if (visible && !running) {
        running = true;
        requestAnimationFrame(tick);
      }
    }).observe(mark);

    function tick(t) {
      if (!visible) {
        running = false;
        return;
      }
      energy *= 0.988; // oozes back down slowly, like cooling wax
      const e = energy * energy * (3 - 2 * energy);
      const breathe = 0.825 + 0.325 * Math.sin(t / 715); // the slow idle melt (~4.5 s)
      const sigma = W * (0.0045 + e * 0.009);
      const slope = Math.max(28, sigma * 7), cut = -slope * (0.45 - e * 0.12);
      disp.setAttribute('scale', (baseScale * (breathe + e * 3.6)).toFixed(1));
      blur.setAttribute('stdDeviation', sigma.toFixed(2));
      goo.setAttribute('values', `1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${slope.toFixed(1)} ${cut.toFixed(1)}`);
      const f = (4.6 / W) * (1 - e * 0.55) * (1 + Math.sin(t / 1035) * 0.2);
      turb.setAttribute('baseFrequency', `${f.toFixed(5)} ${(f * 1.6).toFixed(5)}`);
      requestAnimationFrame(tick);
    }
  }

  const start = () => {
    initGlopMode();
    initLogoWarp();
    initLava();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Footer "Cookie preferences": reopens Shopify's cookie banner preferences
   (window.privacyBanner comes from the banner script). Where no banner
   applies, it falls back to the privacy policy. */
document.addEventListener('click', function (e) {
  var btn = e.target.closest && e.target.closest('[data-cookie-prefs]');
  if (!btn) return;
  if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
    window.privacyBanner.showPreferences();
  } else {
    location.href = '/policies/privacy-policy';
  }
});

/* Contact pop-up (snippets/glop-contact-pop.liquid): any link to /pages/contact opens it.
   Without JS (or on the contact page itself, which shows the form in place) the link
   just navigates. Back from Shopify after a send or an error, it reopens on the result. */
(function () {
  function dialog() {
    return document.querySelector('[data-glop-contact-pop]');
  }
  function open(dlg) {
    if (!dlg.open) dlg.showModal();
  }
  document.addEventListener('click', function (e) {
    var dlg = dialog();
    if (!dlg || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href]');
    if (a && a.target !== '_blank' && a.origin === location.origin && /^\/pages\/contact\/?$/.test(a.pathname)) {
      e.preventDefault();
      // the mobile menu sheet: close it first so the pop-up isn't behind it
      var menu = a.closest('details[open]');
      if (menu) menu.removeAttribute('open');
      open(dlg);
      return;
    }
    if (e.target.closest('[data-glop-contact-close]')) {
      dlg.close();
      return;
    }
    // a click on the backdrop (outside the box) closes it
    if (e.target === dlg) {
      var r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    }
  });
  function reopen() {
    var dlg = dialog();
    if (dlg && location.hash === '#glop-contact-pop' && dlg.querySelector('[data-glop-contact-done]')) open(dlg);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reopen);
  else reopen();
})();
